import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// API call – adjust import as needed
import { fetchMaterialsPrice } from '../../slices/materialsSlice';
// A helper function to group API selections if needed
import { groupBySection } from '../../hooks/groupBySection';

// Images and icons
import leftLeanToIcon from '../../assets/left-lean-to.svg';
import centerSectionIcon from '../../assets/center-section.svg';
import frontLeanToIcon from '../../assets/front-lean-to.svg';
import rightLeanToIcon from '../../assets/right-lean-to.svg';
import backLeanToIcon from '../../assets/back-lean-to.svg';
import doorIcon from '../../assets/door-order.svg';
import windowIcon from '../../assets/window-order.svg';
import frameoutIcon from '../../assets/frameout-order.svg';
import garageDoorIcon from '../../assets/garage-order.svg';
import rollupDoorIcon from '../../assets/rollup-order.svg';
import skylightIcon from '../../assets/skylight-order.svg';
import ventsIcon from '../../assets/vents.svg';
import { X } from 'react-feather';

const STORAGE_CATEGORIES = ['frameouts', 'rollupDoor', 'door', 'window', 'vents'];

export default function DoorWindows({
	onSelectionsChange,
	apiSelections = [],
	centerStorage,
	leftLean,
	rightLean,
	frontLean,
	backLean,
}) {
	const dispatch = useDispatch();
	const prevDataRef = useRef();

	// Which tab/section are we editing?
	const [activeTab, setActiveTab] = useState('centerSection');

	// These states will hold data returned from the API for each category
	const [doorsList, setDoorsList] = useState([]);
	const [windowsList, setWindowsList] = useState([]);
	const [frameoutList, setFrameoutList] = useState([]);
	const [rollupDoorList, setRollupDoorList] = useState([]);
	const [skylightList, setSkylightList] = useState([]);
	const [ventList, setVentList] = useState([]);

	// Static arrays
	const buildingSides = ['Front End', 'Back End', 'Left Side', 'Right Side'];
	const skylightSides = ['Left Side', 'Right Side'];

	// Build a categories object containing the extra information
	// Note: Ensure you use the same state names as declared.
	const categories = {
		door: Array.isArray(doorsList.items)
			? doorsList.items.map((d) => ({
					name: d.name,
					defaultPrice:
						typeof d.defaultPrice === 'object'
							? d.defaultPrice.value
							: d.defaultPrice,
					categoryId: d.categoryId,
                    materialId: d.id,
			  }))
			: [],
		window: Array.isArray(windowsList.items)
			? windowsList.items
					.filter((w) => w?.active === true) // Filter by active: true
					.map((w) => ({
						name: w?.name,
						defaultPrice:
							typeof w?.defaultPrice === 'object'
								? w?.defaultPrice.value
								: w?.defaultPrice,
						categoryId: w?.categoryId,
                        materialId: w?.id,
					}))
			: [],
		frameouts: Array.isArray(frameoutList.items)
			? frameoutList.items.map((f) => ({
					name: f.name,
					defaultPrice:
						typeof f.defaultPrice === 'object'
							? f.defaultPrice.value
							: f.defaultPrice,
					categoryId: f.categoryId,
                    materialId: f.id,
			  }))
			: [],
		vents: Array.isArray(ventList.items)
			? ventList.items.map((v) => ({
					name: v.name,
					defaultPrice:
						typeof v.defaultPrice === 'object'
							? v.defaultPrice.value
							: v.defaultPrice,
					categoryId: v.categoryId,
                    materialId: v.id,
			  }))
			: [],
		garageDoor: [
			{ name: 'Garage Door Type 1', defaultPrice: 0, categoryId: 'garageDoor' },
			{ name: 'Garage Door list', defaultPrice: 0, categoryId: 'garageDoor' },
		],
		rollupDoor: Array.isArray(rollupDoorList.items)
			? rollupDoorList.items.map((r) => ({
					name: r.name,
					defaultPrice:
						typeof r.defaultPrice === 'object'
							? r.defaultPrice.value
							: r.defaultPrice,
					categoryId: r.categoryId,
                    materialId: r.id,
			  }))
			: [],
		// skylighs category 38
		skylight: Array.isArray(skylightList.items)
			? skylightList.items.map((s) => ({
					name: s?.name,
					defaultPrice:
						typeof s?.defaultPrice === 'object'
							? s?.defaultPrice.value
							: s?.defaultPrice,
					categoryId: s?.categoryId,
                    materialId: s?.id,
			  }))
			: [],
	};

	// Initialize react-hook-form with default values for each section.
	const { control, register, watch, reset, setValue } = useForm({
		defaultValues: {
			frontLeanTo: [],
			leftLeanTo: [],
			centerSection: [],
			rightLeanTo: [],
			backLeanTo: [],
			centerStorageItems: [],
			leftStorageItems: [],
			rightStorageItems: [],
			frontStorageItems: [],
			backStorageItems: [],
		},
	});

	// Watch the form data to send changes upward.
	const data = watch();

	// Set up field arrays for each section.
	const {
		fields: frontLeanToFields,
		append: appendFrontLeanTo,
		remove: removeFrontLeanTo,
	} = useFieldArray({ control, name: 'frontLeanTo' });
	const {
		fields: leftLeanToFields,
		append: appendLeftLeanTo,
		remove: removeLeftLeanTo,
	} = useFieldArray({ control, name: 'leftLeanTo' });
	const {
		fields: centerSectionFields,
		append: appendCenterSection,
		remove: removeCenterSection,
	} = useFieldArray({ control, name: 'centerSection' });
	const {
		fields: rightLeanToFields,
		append: appendRightLeanTo,
		remove: removeRightLeanTo,
	} = useFieldArray({ control, name: 'rightLeanTo' });
	const {
		fields: backLeanToFields,
		append: appendBackLeanTo,
		remove: removeBackLeanTo,
	} = useFieldArray({ control, name: 'backLeanTo' });
	const {
		fields: centerStorageFields,
		append: appendCenterStorage,
		remove: removeCenterStorage,
	} = useFieldArray({ control, name: 'centerStorageItems' });
	const {
		fields: leftStorageFields,
		append: appendLeftStorage,
		remove: removeLeftStorage,
	} = useFieldArray({ control, name: 'leftStorageItems' });
	const {
		fields: rightStorageFields,
		append: appendRightStorage,
		remove: removeRightStorage,
	} = useFieldArray({ control, name: 'rightStorageItems' });
	const {
		fields: frontStorageFields,
		append: appendFrontStorage,
		remove: removeFrontStorage,
	} = useFieldArray({ control, name: 'frontStorageItems' });
	const {
		fields: backStorageFields,
		append: appendBackStorage,
		remove: removeBackStorage,
	} = useFieldArray({ control, name: 'backStorageItems' });

	// Handler to change active tab/section.
	const handleTabClick = (tabName) => setActiveTab(tabName);

	// Add item to the current active section.
	const addItem = (category) => {
		const appendMap = {
			frontLeanTo: appendFrontLeanTo,
			leftLeanTo: appendLeftLeanTo,
			centerSection: appendCenterSection,
			rightLeanTo: appendRightLeanTo,
			backLeanTo: appendBackLeanTo,
			centerStorage: appendCenterStorage,
			leftStorage: appendLeftStorage,
			rightStorage: appendRightStorage,
			frontStorage: appendFrontStorage,
			backStorage: appendBackStorage,
		};
		// Get the first option as default.
		const defaultOption = categories[category][0];
		appendMap[activeTab]({
			category,
			type: defaultOption.name,
			buildingSides: buildingSides[0],
			location: { left: '', bottom: '' },
			defaultPrice: defaultOption.defaultPrice,
			categoryId: defaultOption.categoryId,
			materialId: defaultOption.materialId,
			texasCut: false,
		});
	};

	// Fetch the API data for each category.
	useEffect(() => {
		const fetchDoors = async () => {
			const res = await dispatch(
				fetchMaterialsPrice({
					categoryId: 36,
					pageSize: 1000,
					page: 1,
					sortOrder: 'ASC',
				})
			);
			setDoorsList(res?.payload);
		};
		fetchDoors();
	}, [dispatch]);

	useEffect(() => {
		const fetchWindows = async () => {
			const res = await dispatch(
				fetchMaterialsPrice({
					categoryId: 15,
					pageSize: 1000,
					page: 1,
					sortOrder: 'ASC',
				})
			);
			setWindowsList(res.payload);
		};
		fetchWindows();
	}, [dispatch]);

	useEffect(() => {
		const fetchFrameouts = async () => {
			const res = await dispatch(
				fetchMaterialsPrice({
					categoryId: 34,
					pageSize: 1000,
					inventory: false,
					page: 1,
					sortOrder: 'ASC',
				})
			);
			setFrameoutList(res.payload);
		};
		fetchFrameouts();
	}, [dispatch]);

	useEffect(() => {
		const fetchRollupDoors = async () => {
			const res = await dispatch(
				fetchMaterialsPrice({
					categoryId: 34,
					pageSize: 1000,
					page: 1,
					sortOrder: 'ASC',
				})
			);
			setRollupDoorList(res.payload);
		};
		fetchRollupDoors();
	}, [dispatch]);

	// Skylights category 38
	useEffect(() => {
		const fetchSkylights = async () => {
			const res = await dispatch(
				fetchMaterialsPrice({
					categoryId: 38,
					pageSize: 1000,
					page: 1,
					sortOrder: 'ASC',
				})
			);
			setSkylightList(res.payload);
		};
		fetchSkylights();
	}, [dispatch]);

	// vents category 37
	useEffect(() => {
		const fetchVents = async () => {
			const res = await dispatch(
				fetchMaterialsPrice({
					categoryId: 37,
					pageSize: 1000,
					page: 1,
					sortOrder: 'ASC',
				})
			);
			setVentList(res.payload);
		};
		fetchVents();
	}, [dispatch]);

	// If apiSelections are provided, reset the form defaults.
	useEffect(() => {
		if (apiSelections.length) {
			const newDefaults = groupBySection(apiSelections);
			reset(newDefaults);
		}
	}, [apiSelections, reset]);

	const memoizedData = useMemo(() => data, [JSON.stringify(data)]);

	// Whenever the form data changes, compute the selections and pass them upward.
	useEffect(() => {
		const newSelections = [
			...memoizedData.frontLeanTo,
			...memoizedData.leftLeanTo,
			...memoizedData.centerSection,
			...memoizedData.rightLeanTo,
			...memoizedData.backLeanTo,
			...(memoizedData.centerStorageItems || []),
			...(memoizedData.leftStorageItems || []),
			...(memoizedData.rightStorageItems || []),
			...(memoizedData.frontStorageItems || []),
			...(memoizedData.backStorageItems || []),
		].map((selection) => ({
			description: selection.type || 'Unknown',
			quantity: 1,
			categoryId: selection.categoryId,
            materialId: selection.materialId,
			defaultPrice: selection.defaultPrice,
            category: selection.category, // Pass the category for better labeling
			metadata: {
				locationLeft: selection.location?.left || 'Unknown',
				locationBottom: selection.location?.bottom || 'Unknown',
				section: selection.section || 'Unknown',
				buildingSides: selection.buildingSides || 'Unknown',
				texasCut: selection.texasCut || false,
			},
		}));
		onSelectionsChange(newSelections);
		prevDataRef.current = memoizedData;
	}, [memoizedData, onSelectionsChange, activeTab]);

	// Now we render the UI. Below is the example for the frontLeanTo section.
	// (Repeat a similar structure for the other sections as needed.)
	return (
		<div className="new-order-form">
			<p className="instructions-p">
				Select a section or a storage section. Add items using the buttons
				below.
			</p>
			<div className="doors-windows-section">
				{/* Tab Buttons */}
				<ul className="tab-buttons">
					<li
						className={
							activeTab === 'leftLeanTo' ? 'tab-button active' : 'tab-button'
						}
						onClick={() => handleTabClick('leftLeanTo')}
					>
						<img
							src={leftLeanToIcon}
							alt="Left Lean-to"
							className="icon-dwtab"
						/>
						<strong>Left Lean-to</strong>
					</li>
					<li
						className={
							activeTab === 'frontLeanTo' ? 'tab-button active' : 'tab-button'
						}
						onClick={() => handleTabClick('frontLeanTo')}
					>
						<img
							src={frontLeanToIcon}
							alt="Front Lean-to"
							className="icon-dwtab"
						/>
						<strong>Front Lean-to</strong>
					</li>
					<li
						className={
							activeTab === 'centerSection' ? 'tab-button active' : 'tab-button'
						}
						onClick={() => handleTabClick('centerSection')}
					>
						<img
							src={centerSectionIcon}
							alt="Center Section"
							className="icon-dwtab"
						/>
						<strong>Center Section</strong>
					</li>
					<li
						className={
							activeTab === 'backLeanTo' ? 'tab-button active' : 'tab-button'
						}
						onClick={() => handleTabClick('backLeanTo')}
					>
						<img
							src={backLeanToIcon}
							alt="Back Lean-to"
							className="icon-dwtab"
						/>
						<strong>Back Lean-to</strong>
					</li>
					<li
						className={
							activeTab === 'rightLeanTo' ? 'tab-button active' : 'tab-button'
						}
						onClick={() => handleTabClick('rightLeanTo')}
					>
						<img
							src={rightLeanToIcon}
							alt="Right Lean-to"
							className="icon-dwtab"
						/>
						<strong>Right Lean-to</strong>
					</li>
					{centerStorage && centerStorage !== 'none' && (
						<li
							className={activeTab === 'centerStorage' ? 'tab-button active' : 'tab-button'}
							onClick={() => handleTabClick('centerStorage')}
						>
							<strong>Center Storage</strong>
						</li>
					)}
					{leftLean === 'leanToWithStorage' && (
						<li
							className={activeTab === 'leftStorage' ? 'tab-button active' : 'tab-button'}
							onClick={() => handleTabClick('leftStorage')}
						>
							<strong>Left Storage</strong>
						</li>
					)}
					{rightLean === 'leanToWithStorage' && (
						<li
							className={activeTab === 'rightStorage' ? 'tab-button active' : 'tab-button'}
							onClick={() => handleTabClick('rightStorage')}
						>
							<strong>Right Storage</strong>
						</li>
					)}
					{frontLean === 'leanToWithStorage' && (
						<li
							className={activeTab === 'frontStorage' ? 'tab-button active' : 'tab-button'}
							onClick={() => handleTabClick('frontStorage')}
						>
							<strong>Front Storage</strong>
						</li>
					)}
					{backLean === 'leanToWithStorage' && (
						<li
							className={activeTab === 'backStorage' ? 'tab-button active' : 'tab-button'}
							onClick={() => handleTabClick('backStorage')}
						>
							<strong>Back Storage</strong>
						</li>
					)}
				</ul>
				<h3>Add items to wall</h3>
				<p className="instructions-p">
					Item intersects with building leg, a header for support may be
					required at an additional cost.
				</p>
				<div className="tab-windows-doors">
					{/* Here is the frontLeanTo section example */}
					{activeTab === 'frontLeanTo' && (
						<>
							{frontLeanToFields.map((item, index) => (
								<div key={item.id} className="inputs-container">
									<div className="input-group">
										<label className="fisrt-input">
											<img
												src={
													item.category === 'door'
														? doorIcon
														: item.category === 'frameouts'
														? frameoutIcon
														: item.category === 'garageDoor'
														? garageDoorIcon
														: item.category === 'rollupDoor'
														? rollupDoorIcon
														: item.category === 'skylight'
														? skylightIcon
														: item.category === 'vents'
														? ventsIcon
														: windowIcon
												}
												alt={item.type}
												className="icon-dwtab"
											/>
											{item.category === 'door'
												? 'Door'
												: item.category === 'frameouts'
												? 'Frameout'
												: item.category === 'garageDoor'
												? 'Garage Door'
												: item.category === 'rollupDoor'
												? 'Rollup Door'
												: item.category === 'skylight'
												? 'Skylight'
												: item.category === 'vents'
												? 'Vents'
												: 'Window'}
										</label>
										<button
											type="button"
											className="remove-item"
											onClick={() => removeFrontLeanTo(index)}
										>
											<X size={18} />
										</button>
									</div>
									<div className="input-group">
										<div className="input-selection">
											<h3 className="group-title">
												{item.category === 'door'
													? 'Door'
													: item.category === 'frameouts'
													? 'Frameout'
													: item.category === 'garageDoor'
													? 'Garage Door'
													: item.category === 'rollupDoor'
													? 'Rollup Door'
													: item.category === 'skylight'
													? 'Skylight'
													: item.category === 'vents'
													? 'Vents'
													: 'Window'}
											</h3>
											<Controller
												control={control}
												name={`frontLeanTo[${index}].type`}
												defaultValue={item.type}
												render={({ field }) => (
													<select
														{...field}
														className="select-group"
														onChange={(e) => {
															field.onChange(e);
															// When the selection changes, find the option object and update the related fields:
															const selectedOption = categories[
																item.category
															].find((obj) => obj.name === e.target.value);
															if (selectedOption) {
																setValue(
																	`frontLeanTo[${index}].defaultPrice`,
																	selectedOption.defaultPrice
																);
																setValue(
																	`frontLeanTo[${index}].categoryId`,
																	selectedOption.categoryId
																);
                                                                setValue(
																	`frontLeanTo[${index}].materialId`,
																	selectedOption.materialId
																);
															}
														}}
													>
														{categories[item.category] &&
														categories[item.category].length > 0 ? (
															categories[item.category].map((option) => (
																<option key={option.name} value={option.name}>
																	{option.name}
																</option>
															))
														) : (
															<option>Loading...</option>
														)}
													</select>
												)}
											/>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Side</h3>
											<select
												{...register(`frontLeanTo[${index}].buildingSides`)}
												className="select-group"
											>
												{(item.category === 'skylight'
													? skylightSides
													: buildingSides
												).map((side) => (
													<option key={side} value={side}>
														{side}
													</option>
												))}
											</select>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Location</h3>
											<div className="inp-col-size">
												<label>Left</label>
												<input
													{...register(`frontLeanTo[${index}].location.left`)}
													className="select-input"
												/>
											</div>
											<div className="inp-col-size">
												<label>Bottom</label>
												<input
													{...register(`frontLeanTo[${index}].location.bottom`)}
													className="select-input"
												/>
											</div>
											<input
												type="hidden"
												value="frontLeanTo"
												{...register(`frontLeanTo.${index}.section`)}
											/>
										</div>
										{['frameouts', 'rollupDoor', 'garageDoor'].includes(item.category) && (
											<div className="input-selection">
												<h3 className="group-title">Texas Cut</h3>
												<input type="checkbox" {...register(`frontLeanTo[${index}].texasCut`)} />
											</div>
										)}
									</div>
								</div>
							))}
							<div className="door-window-buttons">
								<button
									type="button"
									onClick={() => addItem('door')}
									className="door-window-button"
								>
									<img src={doorIcon} alt="Door" className="icon-dwtab" /> Door
								</button>
								<button
									type="button"
									onClick={() => addItem('window')}
									className="door-window-button"
								>
									<img src={windowIcon} alt="Window" className="icon-dwtab" />{' '}
									Window
								</button>
								<button
									type="button"
									onClick={() => addItem('frameouts')}
									className="door-window-button"
								>
									<img
										src={frameoutIcon}
										alt="Frameout"
										className="icon-dwtab"
									/>{' '}
									Frameout
								</button>
								{/* <button
									type="button"
									onClick={() => addItem('garageDoor')}
									className="door-window-button"
								>
									<img
										src={garageDoorIcon}
										alt="Garage Door"
										className="icon-dwtab"
									/>{' '}
									Garage Door
								</button> */}
								<button
									type="button"
									onClick={() => addItem('rollupDoor')}
									className="door-window-button"
								>
									<img
										src={rollupDoorIcon}
										alt="Rollup Door"
										className="icon-dwtab"
									/>{' '}
									Rollup Door
								</button>
								<button
									type="button"
									onClick={() => addItem('skylight')}
									className="door-window-button"
								>
									<img
										src={skylightIcon}
										alt="Skylight"
										className="icon-dwtab"
									/>{' '}
									Skylight
								</button>
								<button
									type="button"
									onClick={() => addItem('vents')}
									className="door-window-button"
								>
									<img src={ventsIcon} alt="Vents" className="icon-dwtab" />{' '}
									Vents
								</button>
							</div>
						</>
					)}

					{/* leftLeanTo */}
					{activeTab === 'leftLeanTo' && (
						<>
							{leftLeanToFields.map((item, index) => (
								<div key={item.id} className="inputs-container">
									<div className="input-group">
										<label className="fisrt-input">
											<img
												src={
													item.category === 'door'
														? doorIcon
														: item.category === 'frameouts'
														? frameoutIcon
														: item.category === 'garageDoor'
														? garageDoorIcon
														: item.category === 'rollupDoor'
														? rollupDoorIcon
														: item.category === 'skylight'
														? skylightIcon
														: item.category === 'vents'
														? ventsIcon
														: windowIcon
												}
												alt={item.type}
												className="icon-dwtab"
											/>
											{item.category === 'door'
												? 'Door'
												: item.category === 'frameouts'
												? 'Frameout'
												: item.category === 'garageDoor'
												? 'Garage Door'
												: item.category === 'rollupDoor'
												? 'Rollup Door'
												: item.category === 'skylight'
												? 'Skylight'
												: item.category === 'vents'
												? 'Vents'
												: 'Window'}
										</label>
										<button
											type="button"
											className="remove-item"
											onClick={() => removeLeftLeanTo(index)}
										>
											<X size={18} />
										</button>
									</div>
									<div className="input-group">
										<div className="input-selection">
											<h3 className="group-title">
												{item.category === 'door'
													? 'Door'
													: item.category === 'frameouts'
													? 'Frameout'
													: item.category === 'garageDoor'
													? 'Garage Door'
													: item.category === 'rollupDoor'
													? 'Rollup Door'
													: item.category === 'skylight'
													? 'Skylight'
													: item.category === 'vents'
													? 'Vents'
													: 'Window'}
											</h3>
											<Controller
												control={control}
												name={`leftLeanTo[${index}].type`}
												defaultValue={item.type}
												render={({ field }) => (
													<select
														{...field}
														className="select-group"
														onChange={(e) => {
															field.onChange(e);
															// When the selection changes, find the option object and update the related fields:
															const selectedOption = categories[
																item.category
															].find((obj) => obj.name === e.target.value);
															if (selectedOption) {
																setValue(
																	`leftLeanTo[${index}].defaultPrice`,
																	selectedOption.defaultPrice
																);
																setValue(
																	`leftLeanTo[${index}].categoryId`,
																	selectedOption.categoryId
																);
                                                                setValue(
																	`leftLeanTo[${index}].materialId`,
																	selectedOption.materialId
																);
															}
														}}
													>
														{categories[item.category] &&
														categories[item.category].length > 0 ? (
															categories[item.category].map((option) => (
																<option key={option.name} value={option.name}>
																	{option.name}
																</option>
															))
														) : (
															<option>Loading...</option>
														)}
													</select>
												)}
											/>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Side</h3>
											<select
												{...register(`leftLeanTo[${index}].buildingSides`)}
												className="select-group"
											>
												{(item.category === 'skylight'
													? skylightSides
													: buildingSides
												).map((side) => (
													<option key={side} value={side}>
														{side}
													</option>
												))}
											</select>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Location</h3>
											<div className="inp-col-size">
												<label>Left</label>
												<input
													{...register(`leftLeanTo[${index}].location.left`)}
													className="select-input"
												/>
											</div>
											<div className="inp-col-size">
												<label>Bottom</label>
												<input
													{...register(`leftLeanTo[${index}].location.bottom`)}
													className="select-input"
												/>
											</div>
											<input
												type="hidden"
												value="leftLeanTo"
												{...register(`leftLeanTo.${index}.section`)}
											/>
										</div>
										{['frameouts', 'rollupDoor', 'garageDoor'].includes(item.category) && (
											<div className="input-selection">
												<h3 className="group-title">Texas Cut</h3>
												<input type="checkbox" {...register(`leftLeanTo[${index}].texasCut`)} />
											</div>
										)}
									</div>
								</div>
							))}
							<div className="door-window-buttons">
								<button
									type="button"
									onClick={() => addItem('door')}
									className="door-window-button"
								>
									<img src={doorIcon} alt="Door" className="icon-dwtab" /> Door
								</button>
								<button
									type="button"
									onClick={() => addItem('window')}
									className="door-window-button"
								>
									<img src={windowIcon} alt="Window" className="icon-dwtab" />{' '}
									Window
								</button>
								<button
									type="button"
									onClick={() => addItem('frameouts')}
									className="door-window-button"
								>
									<img
										src={frameoutIcon}
										alt="Frameout"
										className="icon-dwtab"
									/>{' '}
									Frameout
								</button>
								{/* <button
									type="button"
									onClick={() => addItem('garageDoor')}
									className="door-window-button"
								>
									<img
										src={garageDoorIcon}
										alt="Garage Door"
										className="icon-dwtab"
									/>{' '}
									Garage Door
								</button> */}
								<button
									type="button"
									onClick={() => addItem('rollupDoor')}
									className="door-window-button"
								>
									<img
										src={rollupDoorIcon}
										alt="Rollup Door"
										className="icon-dwtab"
									/>{' '}
									Rollup Door
								</button>
								<button
									type="button"
									onClick={() => addItem('skylight')}
									className="door-window-button"
								>
									<img
										src={skylightIcon}
										alt="Skylight"
										className="icon-dwtab"
									/>{' '}
									Skylight
								</button>
								<button
									type="button"
									onClick={() => addItem('vents')}
									className="door-window-button"
								>
									<img src={ventsIcon} alt="Vents" className="icon-dwtab" />{' '}
									Vents
								</button>
							</div>
						</>
					)}
					{/* centerSection */}
					{activeTab === 'centerSection' && (
						<>
							{centerSectionFields.map((item, index) => (
								<div key={item.id} className="inputs-container">
									<div className="input-group">
										<label className="fisrt-input">
											<img
												src={
													item.category === 'door'
														? doorIcon
														: item.category === 'frameouts'
														? frameoutIcon
														: item.category === 'garageDoor'
														? garageDoorIcon
														: item.category === 'rollupDoor'
														? rollupDoorIcon
														: item.category === 'skylight'
														? skylightIcon
														: item.category === 'vents'
														? ventsIcon
														: windowIcon
												}
												alt={item.type}
												className="icon-dwtab"
											/>
											{item.category === 'door'
												? 'Door'
												: item.category === 'frameouts'
												? 'Frameout'
												: item.category === 'garageDoor'
												? 'Garage Door'
												: item.category === 'rollupDoor'
												? 'Rollup Door'
												: item.category === 'skylight'
												? 'Skylight'
												: item.category === 'vents'
												? 'Vents'
												: 'Window'}
										</label>
										<button
											type="button"
											className="remove-item"
											onClick={() => removeCenterSection(index)}
										>
											<X size={18} />
										</button>
									</div>
									<div className="input-group">
										<div className="input-selection">
											<h3 className="group-title">
												{item.category === 'door'
													? 'Door'
													: item.category === 'frameouts'
													? 'Frameout'
													: item.category === 'garageDoor'
													? 'Garage Door'
													: item.category === 'rollupDoor'
													? 'Rollup Door'
													: item.category === 'skylight'
													? 'Skylight'
													: item.category === 'vents'
													? 'Vents'
													: 'Window'}
											</h3>
											<Controller
												control={control}
												name={`centerSection[${index}].type`}
												defaultValue={item.type}
												render={({ field }) => (
													<select
														{...field}
														className="select-group"
														onChange={(e) => {
															field.onChange(e);
															// When the selection changes, find the option object and update the related fields:
															const selectedOption = categories[
																item.category
															].find((obj) => obj.name === e.target.value);
															if (selectedOption) {
																setValue(
																	`centerSection[${index}].defaultPrice`,
																	selectedOption.defaultPrice
																);
																setValue(
																	`centerSection[${index}].categoryId`,
																	selectedOption.categoryId
																);
                                                                setValue(
																	`centerSection[${index}].materialId`,
																	selectedOption.materialId
																);
															}
														}}
													>
														{categories[item.category] &&
														categories[item.category].length > 0 ? (
															categories[item.category].map((option) => (
																<option key={option.name} value={option.name}>
																	{option.name}
																</option>
															))
														) : (
															<option>Loading...</option>
														)}
													</select>
												)}
											/>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Side</h3>
											<select
												{...register(`centerSection[${index}].buildingSides`)}
												className="select-group"
											>
												{(item.category === 'skylight'
													? skylightSides
													: buildingSides
												).map((side) => (
													<option key={side} value={side}>
														{side}
													</option>
												))}
											</select>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Location</h3>
											<div className="inp-col-size">
												<label>Left</label>
												<input
													{...register(`centerSection[${index}].location.left`)}
													className="select-input"
												/>
											</div>
											<div className="inp-col-size">
												<label>Bottom</label>
												<input
													{...register(
														`centerSection[${index}].location.bottom`
													)}
													className="select-input"
												/>
											</div>
											<input
												type="hidden"
												value="centerSection"
												{...register(`centerSection.${index}.section`)}
											/>
										</div>
										{['frameouts', 'rollupDoor', 'garageDoor'].includes(item.category) && (
											<div className="input-selection">
												<h3 className="group-title">Texas Cut</h3>
												<input type="checkbox" {...register(`centerSection[${index}].texasCut`)} />
											</div>
										)}
									</div>
								</div>
							))}
							<div className="door-window-buttons">
								<button
									type="button"
									onClick={() => addItem('door')}
									className="door-window-button"
								>
									<img src={doorIcon} alt="Door" className="icon-dwtab" /> Door
								</button>
								<button
									type="button"
									onClick={() => addItem('window')}
									className="door-window-button"
								>
									<img src={windowIcon} alt="Window" className="icon-dwtab" />{' '}
									Window
								</button>
								<button
									type="button"
									onClick={() => addItem('frameouts')}
									className="door-window-button"
								>
									<img
										src={frameoutIcon}
										alt="Frameout"
										className="icon-dwtab"
									/>{' '}
									Frameout
								</button>
								{/* <button
									type="button"
									onClick={() => addItem('garageDoor')}
									className="door-window-button"
								>
									<img
										src={garageDoorIcon}
										alt="Garage Door"
										className="icon-dwtab"
									/>{' '}
									Garage Door
								</button> */}
								<button
									type="button"
									onClick={() => addItem('rollupDoor')}
									className="door-window-button"
								>
									<img
										src={rollupDoorIcon}
										alt="Rollup Door"
										className="icon-dwtab"
									/>{' '}
									Rollup Door
								</button>
								<button
									type="button"
									onClick={() => addItem('skylight')}
									className="door-window-button"
								>
									<img
										src={skylightIcon}
										alt="Skylight"
										className="icon-dwtab"
									/>{' '}
									Skylight
								</button>
								<button
									type="button"
									onClick={() => addItem('vents')}
									className="door-window-button"
								>
									<img src={ventsIcon} alt="Vents" className="icon-dwtab" />{' '}
									Vents
								</button>
							</div>
						</>
					)}

					{/* RightLeanTo */}
					{activeTab === 'rightLeanTo' && (
						<>
							{rightLeanToFields.map((item, index) => (
								<div key={item.id} className="inputs-container">
									<div className="input-group">
										<label className="fisrt-input">
											<img
												src={
													item.category === 'door'
														? doorIcon
														: item.category === 'frameouts'
														? frameoutIcon
														: item.category === 'garageDoor'
														? garageDoorIcon
														: item.category === 'rollupDoor'
														? rollupDoorIcon
														: item.category === 'skylight'
														? skylightIcon
														: item.category === 'vents'
														? ventsIcon
														: windowIcon
												}
												alt={item.type}
												className="icon-dwtab"
											/>
											{item.category === 'door'
												? 'Door'
												: item.category === 'frameouts'
												? 'Frameout'
												: item.category === 'garageDoor'
												? 'Garage Door'
												: item.category === 'rollupDoor'
												? 'Rollup Door'
												: item.category === 'skylight'
												? 'Skylight'
												: item.category === 'vents'
												? 'Vents'
												: 'Window'}
										</label>
										<button
											type="button"
											className="remove-item"
											onClick={() => removeRightLeanTo(index)}
										>
											<X size={18} />
										</button>
									</div>
									<div className="input-group">
										<div className="input-selection">
											<h3 className="group-title">
												{item.category === 'door'
													? 'Door'
													: item.category === 'frameouts'
													? 'Frameout'
													: item.category === 'garageDoor'
													? 'Garage Door'
													: item.category === 'rollupDoor'
													? 'Rollup Door'
													: item.category === 'skylight'
													? 'Skylight'
													: item.category === 'vents'
													? 'Vents'
													: 'Window'}
											</h3>
											<Controller
												control={control}
												name={`rightLeanTo[${index}].type`}
												defaultValue={item.type}
												render={({ field }) => (
													<select
														{...field}
														className="select-group"
														onChange={(e) => {
															field.onChange(e);
															// When the selection changes, find the option object and update the related fields:
															const selectedOption = categories[
																item.category
															].find((obj) => obj.name === e.target.value);
															if (selectedOption) {
																setValue(
																	`rightLeanTo[${index}].defaultPrice`,
																	selectedOption.defaultPrice
																);
																setValue(
																	`rightLeanTo[${index}].categoryId`,
																	selectedOption.categoryId
																);
                                                                setValue(
																	`rightLeanTo[${index}].materialId`,
																	selectedOption.materialId
																);
															}
														}}
													>
														{categories[item.category] &&
														categories[item.category].length > 0 ? (
															categories[item.category].map((option) => (
																<option key={option.name} value={option.name}>
																	{option.name}
																</option>
															))
														) : (
															<option>Loading...</option>
														)}
													</select>
												)}
											/>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Side</h3>
											<select
												{...register(`rightLeanTo[${index}].buildingSides`)}
												className="select-group"
											>
												{(item.category === 'skylight'
													? skylightSides
													: buildingSides
												).map((side) => (
													<option key={side} value={side}>
														{side}
													</option>
												))}
											</select>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Location</h3>
											<div className="inp-col-size">
												<label>Left</label>
												<input
													{...register(`rightLeanTo[${index}].location.left`)}
													className="select-input"
												/>
											</div>
											<div className="inp-col-size">
												<label>Bottom</label>
												<input
													{...register(`rightLeanTo[${index}].location.bottom`)}
													className="select-input"
												/>
											</div>
											<input
												type="hidden"
												value="rightLeanTo"
												{...register(`rightLeanTo.${index}.section`)}
											/>
										</div>
										{['frameouts', 'rollupDoor', 'garageDoor'].includes(item.category) && (
											<div className="input-selection">
												<h3 className="group-title">Texas Cut</h3>
												<input type="checkbox" {...register(`rightLeanTo[${index}].texasCut`)} />
											</div>
										)}
									</div>
								</div>
							))}
							<div className="door-window-buttons">
								<button
									type="button"
									onClick={() => addItem('door')}
									className="door-window-button"
								>
									<img src={doorIcon} alt="Door" className="icon-dwtab" /> Door
								</button>
								<button
									type="button"
									onClick={() => addItem('window')}
									className="door-window-button"
								>
									<img src={windowIcon} alt="Window" className="icon-dwtab" />{' '}
									Window
								</button>
								<button
									type="button"
									onClick={() => addItem('frameouts')}
									className="door-window-button"
								>
									<img
										src={frameoutIcon}
										alt="Frameout"
										className="icon-dwtab"
									/>{' '}
									Frameout
								</button>
								{/* <button
									type="button"
									onClick={() => addItem('garageDoor')}
									className="door-window-button"
								>
									<img
										src={garageDoorIcon}
										alt="Garage Door"
										className="icon-dwtab"
									/>{' '}
									Garage Door
								</button> */}
								<button
									type="button"
									onClick={() => addItem('rollupDoor')}
									className="door-window-button"
								>
									<img
										src={rollupDoorIcon}
										alt="Rollup Door"
										className="icon-dwtab"
									/>{' '}
									Rollup Door
								</button>
								<button
									type="button"
									onClick={() => addItem('skylight')}
									className="door-window-button"
								>
									<img
										src={skylightIcon}
										alt="Skylight"
										className="icon-dwtab"
									/>{' '}
									Skylight
								</button>
								<button
									type="button"
									onClick={() => addItem('vents')}
									className="door-window-button"
								>
									<img src={ventsIcon} alt="Vents" className="icon-dwtab" />{' '}
									Vents
								</button>
							</div>
						</>
					)}
					{/* backLeanTo  */}
					{activeTab === 'backLeanTo' && (
						<>
							{backLeanToFields.map((item, index) => (
								<div key={item.id} className="inputs-container">
									<div className="input-group">
										<label className="fisrt-input">
											<img
												src={
													item.category === 'door'
														? doorIcon
														: item.category === 'frameouts'
														? frameoutIcon
														: item.category === 'garageDoor'
														? garageDoorIcon
														: item.category === 'rollupDoor'
														? rollupDoorIcon
														: item.category === 'skylight'
														? skylightIcon
														: item.category === 'vents'
														? ventsIcon
														: windowIcon
												}
												alt={item.type}
												className="icon-dwtab"
											/>
											{item.category === 'door'
												? 'Door'
												: item.category === 'frameouts'
												? 'Frameout'
												: item.category === 'garageDoor'
												? 'Garage Door'
												: item.category === 'rollupDoor'
												? 'Rollup Door'
												: item.category === 'skylight'
												? 'Skylight'
												: item.category === 'vents'
												? 'Vents'
												: 'Window'}
										</label>
										<button
											type="button"
											className="remove-item"
											onClick={() => removeBackLeanTo(index)}
										>
											<X size={18} />
										</button>
									</div>
									<div className="input-group">
										<div className="input-selection">
											<h3 className="group-title">
												{item.category === 'door'
													? 'Door'
													: item.category === 'frameouts'
													? 'Frameout'
													: item.category === 'garageDoor'
													? 'Garage Door'
													: item.category === 'rollupDoor'
													? 'Rollup Door'
													: item.category === 'skylight'
													? 'Skylight'
													: item.category === 'vents'
													? 'Vents'
													: 'Window'}
											</h3>
											<Controller
												control={control}
												name={`backLeanTo[${index}].type`}
												defaultValue={item.type}
												render={({ field }) => (
													<select
														{...field}
														className="select-group"
														onChange={(e) => {
															field.onChange(e);
															// When the selection changes, find the option object and update the related fields:
															const selectedOption = categories[
																item.category
															].find((obj) => obj.name === e.target.value);
															if (selectedOption) {
																setValue(
																	`backLeanTo[${index}].defaultPrice`,
																	selectedOption.defaultPrice
																);
																setValue(
																	`backLeanTo[${index}].categoryId`,
																	selectedOption.categoryId
																);
                                                                setValue(
																	`backLeanTo[${index}].materialId`,
																	selectedOption.materialId
																);
															}
														}}
													>
														{categories[item.category] &&
														categories[item.category].length > 0 ? (
															categories[item.category].map((option) => (
																<option key={option.name} value={option.name}>
																	{option.name}
																</option>
															))
														) : (
															<option>Loading...</option>
														)}
													</select>
												)}
											/>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Side</h3>
											<select
												{...register(`backLeanTo[${index}].buildingSides`)}
												className="select-group"
											>
												{(item.category === 'skylight'
													? skylightSides
													: buildingSides
												).map((side) => (
													<option key={side} value={side}>
														{side}
													</option>
												))}
											</select>
										</div>
										<div className="input-selection">
											<h3 className="group-title">Location</h3>
											<div className="inp-col-size">
												<label>Left</label>
												<input
													{...register(`backLeanTo[${index}].location.left`)}
													className="select-input"
												/>
											</div>
											<div className="inp-col-size">
												<label>Bottom</label>
												<input
													{...register(`backLeanTo[${index}].location.bottom`)}
													className="select-input"
												/>
											</div>
											<input
												type="hidden"
												value="backLeanTo"
												{...register(`backLeanTo.${index}.section`)}
											/>
										</div>
										{['frameouts', 'rollupDoor', 'garageDoor'].includes(item.category) && (
											<div className="input-selection">
												<h3 className="group-title">Texas Cut</h3>
												<input type="checkbox" {...register(`backLeanTo[${index}].texasCut`)} />
											</div>
										)}
									</div>
								</div>
							))}
							<div className="door-window-buttons">
								<button
									type="button"
									onClick={() => addItem('door')}
									className="door-window-button"
								>
									<img src={doorIcon} alt="Door" className="icon-dwtab" /> Door
								</button>
								<button
									type="button"
									onClick={() => addItem('window')}
									className="door-window-button"
								>
									<img src={windowIcon} alt="Window" className="icon-dwtab" />{' '}
									Window
								</button>
								<button
									type="button"
									onClick={() => addItem('frameouts')}
									className="door-window-button"
								>
									<img
										src={frameoutIcon}
										alt="Frameout"
										className="icon-dwtab"
									/>{' '}
									Frameout
								</button>
								{/* <button
									type="button"
									onClick={() => addItem('garageDoor')}
									className="door-window-button"
								>
									<img
										src={garageDoorIcon}
										alt="Garage Door"
										className="icon-dwtab"
									/>{' '}
									Garage Door
								</button> */}
								<button
									type="button"
									onClick={() => addItem('rollupDoor')}
									className="door-window-button"
								>
									<img
										src={rollupDoorIcon}
										alt="Rollup Door"
										className="icon-dwtab"
									/>{' '}
									Rollup Door
								</button>
								<button
									type="button"
									onClick={() => addItem('skylight')}
									className="door-window-button"
								>
									<img
										src={skylightIcon}
										alt="Skylight"
										className="icon-dwtab"
									/>{' '}
									Skylight
								</button>
								<button
									type="button"
									onClick={() => addItem('vents')}
									className="door-window-button"
								>
									<img src={ventsIcon} alt="Vents" className="icon-dwtab" />{' '}
									Vents
								</button>
							</div>
						</>
					)}

					{/* Storage sections */}
					{[
						{ key: 'centerStorage', visible: centerStorage && centerStorage !== 'none', fields: centerStorageFields, remove: removeCenterStorage, formKey: 'centerStorageItems', label: 'Center Storage' },
						{ key: 'leftStorage',   visible: leftLean === 'leanToWithStorage',  fields: leftStorageFields,   remove: removeLeftStorage,   formKey: 'leftStorageItems',   label: 'Left Storage' },
						{ key: 'rightStorage',  visible: rightLean === 'leanToWithStorage', fields: rightStorageFields,  remove: removeRightStorage,  formKey: 'rightStorageItems',  label: 'Right Storage' },
						{ key: 'frontStorage',  visible: frontLean === 'leanToWithStorage', fields: frontStorageFields,  remove: removeFrontStorage,  formKey: 'frontStorageItems',  label: 'Front Storage' },
						{ key: 'backStorage',   visible: backLean === 'leanToWithStorage',  fields: backStorageFields,   remove: removeBackStorage,   formKey: 'backStorageItems',   label: 'Back Storage' },
					].map(({ key, visible, fields, remove, formKey, label }) => {
						if (!visible || activeTab !== key) return null;
						return (
							<React.Fragment key={key}>
								{fields.map((item, index) => (
									<div key={item.id} className="inputs-container">
										<div className="input-group">
											<label className="fisrt-input">
												<img
													src={item.category === 'door' ? doorIcon : item.category === 'frameouts' ? frameoutIcon : item.category === 'rollupDoor' ? rollupDoorIcon : item.category === 'vents' ? ventsIcon : windowIcon}
													alt={item.category} className="icon-dwtab"
												/>
												{item.category === 'door' ? 'Door' : item.category === 'frameouts' ? 'Frameout' : item.category === 'rollupDoor' ? 'Rollup Door' : item.category === 'vents' ? 'Vents' : 'Window'}
											</label>
											<button type="button" className="remove-item" onClick={() => remove(index)}><X size={18} /></button>
										</div>
										<div className="input-group">
											<div className="input-selection">
												<h3 className="group-title">{label}</h3>
												<Controller control={control} name={`${formKey}[${index}].type`} defaultValue={item.type}
													render={({ field }) => (
														<select {...field} className="select-group"
															onChange={(e) => {
																field.onChange(e);
																const sel = categories[item.category]?.find(o => o.name === e.target.value);
																if (sel) {
																	setValue(`${formKey}[${index}].defaultPrice`, sel.defaultPrice);
																	setValue(`${formKey}[${index}].categoryId`, sel.categoryId);
																	setValue(`${formKey}[${index}].materialId`, sel.materialId);
																}
															}}
														>
															{categories[item.category]?.length > 0
																? categories[item.category].map(o => <option key={o.name} value={o.name}>{o.name}</option>)
																: <option>Loading...</option>}
														</select>
													)}
												/>
											</div>
											<div className="input-selection">
												<h3 className="group-title">Side</h3>
												<select {...register(`${formKey}[${index}].buildingSides`)} className="select-group">
													{buildingSides.map(side => <option key={side} value={side}>{side}</option>)}
												</select>
											</div>
											<div className="input-selection">
												<h3 className="group-title">Location</h3>
												<div className="inp-col-size">
													<label>Left</label>
													<input {...register(`${formKey}[${index}].location.left`)} className="select-input" />
												</div>
												<div className="inp-col-size">
													<label>Bottom</label>
													<input {...register(`${formKey}[${index}].location.bottom`)} className="select-input" />
												</div>
												<input type="hidden" value={key} {...register(`${formKey}.${index}.section`)} />
											</div>
											{['frameouts', 'rollupDoor', 'garageDoor'].includes(item.category) && (
												<div className="input-selection">
													<h3 className="group-title">Texas Cut</h3>
													<input type="checkbox" {...register(`${formKey}[${index}].texasCut`)} />
												</div>
											)}
										</div>
									</div>
								))}
								<div className="door-window-buttons">
									<button type="button" onClick={() => addItem('door')} className="door-window-button">
										<img src={doorIcon} alt="Door" className="icon-dwtab" /> Door
									</button>
									<button type="button" onClick={() => addItem('window')} className="door-window-button">
										<img src={windowIcon} alt="Window" className="icon-dwtab" /> Window
									</button>
									<button type="button" onClick={() => addItem('frameouts')} className="door-window-button">
										<img src={frameoutIcon} alt="Frameout" className="icon-dwtab" /> Frameout
									</button>
									<button type="button" onClick={() => addItem('rollupDoor')} className="door-window-button">
										<img src={rollupDoorIcon} alt="Rollup Door" className="icon-dwtab" /> Rollup Door
									</button>
									<button type="button" onClick={() => addItem('vents')} className="door-window-button">
										<img src={ventsIcon} alt="Vents" className="icon-dwtab" /> Vents
									</button>
								</div>
							</React.Fragment>
						);
					})}
				</div>
			</div>
		</div>
	);
}
